import "./CompanyBilling.css";
import InvoiceViewer from "./components/invoices/InvoiceViewer";
import { usePermissions } from "./PermissionContext";

import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";

const API_URL = "/api";

const initialInvoiceForm = {
  sale_id: "",
  customer_name: "",
  customer_document: "",
  customer_email: "",
  due_date: "",
  tax: "0",
  notes: "",
};

const initialPaymentForm = {
  payment_method: "cash",
  amount: "",
  note: "",
};

const paymentAttemptStorageKey = (invoiceId) =>
  `netledger:invoice-payment:${invoiceId}`;

const getStoredPaymentAttempt = (invoiceId) => {
  try {
    const raw = sessionStorage.getItem(paymentAttemptStorageKey(invoiceId));
    if (!raw) return null;

    const attempt = JSON.parse(raw);
    return attempt?.idempotency_key && attempt?.payload
      ? attempt
      : null;
  } catch {
    return null;
  }
};

const money = (value) =>
  new Intl.NumberFormat("es-CO", {
    style: "currency",
    currency: "COP",
    minimumFractionDigits: 0,
  }).format(Number(value || 0));

const getHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("company_token")}`,
});

const getResponseItems = (data) => {
  if (Array.isArray(data)) return data;

  return data?.items ?? data?.data ?? [];
};

const getErrorMessage = (error) => {
  const detail = error.response?.data?.detail;

  if (typeof detail === "string") return detail;

  if (Array.isArray(detail)) {
    return detail.map((item) => item.msg).join(". ");
  }

  return "Ocurrió un error. Intenta nuevamente.";
};

const statusLabel = (status) => {
  const labels = {
    issued: "Pendiente",
    partial_paid: "Pago parcial",
    paid: "Pagada",
    overdue: "Vencida",
    cancelled: "Anulada",
  };

  return labels[status] || status;
};

function CompanyBilling() {
  const { can } = usePermissions();

  const canManageBilling = can("billing.manage");
  const [summary, setSummary] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [invoiceSettings, setInvoiceSettings] = useState(null);
  const [sales, setSales] = useState([]);

  const [invoiceForm, setInvoiceForm] = useState(initialInvoiceForm);
  const [paymentForm, setPaymentForm] = useState(initialPaymentForm);

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [onlyOverdue, setOnlyOverdue] = useState(false);

  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showInvoiceForm, setShowInvoiceForm] = useState(false);
  const [showPaymentForm, setShowPaymentForm] = useState(false);

  const [loading, setLoading] = useState(true);
  const [savingInvoice, setSavingInvoice] = useState(false);
  const [savingPayment, setSavingPayment] = useState(false);
  const paymentSubmittingRef = useRef(false);


  const loadSummary = useCallback(async () => {
    const response = await axios.get(
      `${API_URL}/invoices/me/summary`,
      { headers: getHeaders() }
    );

    setSummary(response.data);
  }, []);

  const loadSales = useCallback(async () => {
    const response = await axios.get(
      `${API_URL}/sales/me`,
      {
        headers: getHeaders(),
        params: { limit: 100 },
      }
    );

    setSales(getResponseItems(response.data));
  }, []);

  const loadInvoices = useCallback(async () => {
    const response = await axios.get(
      `${API_URL}/invoices/me`,
      {
        headers: getHeaders(),
        params: {
          search: search || undefined,
          status: statusFilter || undefined,
          only_overdue: onlyOverdue,
          limit: 100,
          offset: 0,
        },
      }
    );

    setInvoices(getResponseItems(response.data));
  }, [search, statusFilter, onlyOverdue]);

  const loadInvoiceSettings = useCallback(async () => {

    const response = await axios.get(
      `${API_URL}/settings/invoice/me`,
      {
        headers: getHeaders(),
      }
    );

    setInvoiceSettings(response.data);

  }, []);

  const loadInitialData = useCallback(async () => {
    setLoading(true);

    try {
      await Promise.all([
        loadSummary(),
        loadSales(),
        loadInvoices(),
        loadInvoiceSettings(),
      ]);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  }, [
    loadSummary,
    loadSales,
    loadInvoices,
    loadInvoiceSettings,
  ]);

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  const refreshBilling = async () => {
    try {
      await Promise.all([
        loadSummary(),
        loadSales(),
        loadInvoices(),
        loadInvoiceSettings(),
      ]);
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleInvoiceFormChange = (event) => {
    const { name, value } = event.target;

    setInvoiceForm((current) => ({
      ...current,
      [name]: value,
    }));
  };

  const handlePaymentFormChange = (event) => {
    const { name, value } = event.target;

    setPaymentForm((current) => ({
      ...current,
      [name]: value,
    }));
  };

  const issueInvoice = async (event) => {
    event.preventDefault();
    if (!canManageBilling) {
      toast.error("No tienes permiso para gestionar la facturación.");
      return;
    }

    setSavingInvoice(true);
    // setMessage(null);

    try {
      const response = await axios.post(
        `${API_URL}/invoices/me/from-sale`,
        {
          sale_id: Number(invoiceForm.sale_id),
          customer_name: invoiceForm.customer_name.trim(),
          customer_document:
            invoiceForm.customer_document.trim() || null,
          customer_email:
            invoiceForm.customer_email.trim() || null,
          due_date: invoiceForm.due_date || null,
          tax: Number(invoiceForm.tax || 0),
          notes: invoiceForm.notes.trim() || null,
        },
        {
          headers: getHeaders(),
        }
      );

      toast.success(response.data.message);

      setInvoiceForm(initialInvoiceForm);
      setShowInvoiceForm(false);

      await Promise.all([
        refreshBilling(),
        loadInvoiceSettings(),
      ]);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setSavingInvoice(false);
    }
  };

  const openInvoiceDetail = async (
    invoiceId,
    openPayment = false
  ) => {
    try {
      const response = await axios.get(
        `${API_URL}/invoices/${invoiceId}`,
        {
          headers: getHeaders(),
        }
      );

      setSelectedInvoice(response.data);
      setShowPaymentForm(openPayment);

      const previousAttempt = openPayment
        ? getStoredPaymentAttempt(invoiceId)
        : null;

      setPaymentForm(
        previousAttempt?.payload ?? {
          ...initialPaymentForm,
          amount: Number(
            response.data.invoice?.pending_amount ??
            response.data.pending_amount ??
            0
          ),
        }
      );
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };
  const openInvoiceModal = () => {

    if (!invoiceSettings) {
      toast.error("No se encontró la configuración de facturación.");
      return;
    }

    if (!invoiceSettings.is_active) {
      toast.error("La facturación se encuentra deshabilitada.");
      return;
    }

    if (!invoiceSettings.resolution_number) {
      toast.error("Debe registrar el número de resolución.");
      return;
    }

    if (!invoiceSettings.authorized_from) {
      toast.error("Debe configurar el rango autorizado inicial.");
      return;
    }

    if (!invoiceSettings.authorized_to) {
      toast.error("Debe configurar el rango autorizado final.");
      return;
    }

    if (!invoiceSettings.valid_from) {
      toast.error("Debe registrar la fecha inicial de vigencia.");
      return;
    }

    if (!invoiceSettings.valid_until) {
      toast.error("Debe registrar la fecha final de vigencia.");
      return;
    }

    const today = new Date();
    const start = new Date(invoiceSettings.valid_from);
    const end = new Date(invoiceSettings.valid_until);

    if (today < start) {
      toast.error("La resolución aún no entra en vigencia.");
      return;
    }

    if (today > end) {
      toast.error("La resolución se encuentra vencida.");
      return;
    }

    // Fecha de vencimiento automática
    const dueDate = new Date();
    dueDate.setDate(
      dueDate.getDate() +
      (invoiceSettings.default_due_days || 30)
    );

    setInvoiceForm({
      ...initialInvoiceForm,
      due_date: dueDate.toISOString().split("T")[0],
    });

    setShowInvoiceForm(true);
  };

  const registerPayment = async (event) => {
    event.preventDefault();

    if (!canManageBilling) {
      toast.error("No tienes permiso para gestionar la facturación.");
      return;
    }

    if (!selectedInvoice || paymentSubmittingRef.current) return;

    const invoiceId = selectedInvoice.invoice.id;
    const storageKey = paymentAttemptStorageKey(invoiceId);
    const payload = {
      payment_method: paymentForm.payment_method,
      amount: Number(paymentForm.amount),
      note: (paymentForm.note || "").trim() || null,
    };

    paymentSubmittingRef.current = true;
    setSavingPayment(true);

    try {
      let attempt = getStoredPaymentAttempt(invoiceId);

      if (attempt && JSON.stringify(attempt.payload) !== JSON.stringify(payload)) {
        toast.error(
          "Hay un abono pendiente de confirmar con otros datos. " +
          "Cierra y vuelve a abrir la ventana para reintentar ese envío."
        );
        return;
      }

      if (!attempt) {
        if (!window.crypto?.randomUUID) {
          throw new Error("Este navegador no permite generar una clave segura.");
        }

        attempt = {
          idempotency_key: window.crypto.randomUUID(),
          payload,
        };
        sessionStorage.setItem(storageKey, JSON.stringify(attempt));
      }

      const response = await axios.post(
        `${API_URL}/invoices/${invoiceId}/payments`,
        {
          ...attempt.payload,
          idempotency_key: attempt.idempotency_key,
        },
        { headers: getHeaders() }
      );

      sessionStorage.removeItem(storageKey);
      setSelectedInvoice(null);
      setShowPaymentForm(false);
      setPaymentForm(initialPaymentForm);
      toast.success(response.data.message);

      // Un fallo al actualizar la lista no debe presentarse como
      // si hubiera fallado el registro del abono.
      try {
        await refreshBilling();
      } catch {
        toast.error(
          "El abono se registró, pero no se pudo actualizar la lista. " +
          "Pulsa Actualizar."
        );
      }
    } catch (error) {
      // Estos errores tienen respuesta definitiva del servidor.
      // Ante un error de red o 5xx conservamos la clave: el pago
      // podría haberse confirmado antes de perderse la respuesta.
      if ([400, 401, 403, 404, 422].includes(error.response?.status)) {
        sessionStorage.removeItem(storageKey);
      }

      toast.error(
        error.response
          ? getErrorMessage(error)
          : error.message || "No se pudo confirmar el abono. Reintenta sin cambiar los datos."
      );
    } finally {
      paymentSubmittingRef.current = false;
      setSavingPayment(false);
    }
  };

  if (loading) {
    return <h2>Cargando facturación...</h2>;
  }

  return (
    <div className="company-billing">
      <section className="billing-heading">
        <div>
          <p className="billing-kicker">Facturación</p>
          <h1>Facturas y cartera</h1>
          <p>Emite facturas, registra abonos y controla vencimientos.</p>
        </div>
        {canManageBilling && (
          <button
            type="button"
            className="btn-create-invoice"
            onClick={openInvoiceModal}
          >
            + Emitir factura
          </button>
        )}
      </section>

      <section className="billing-summary-grid">
        <article className="billing-summary-card">
          <span>Facturas emitidas</span>
          <strong>{summary?.total_invoices ?? 0}</strong>
        </article>

        <article className="billing-summary-card">
          <span>Total facturado</span>
          <strong>{money(summary?.total_billed)}</strong>
        </article>

        <article className="billing-summary-card pending">
          <span>Saldo pendiente</span>
          <strong>{money(summary?.total_pending)}</strong>
          <small>
            {summary?.pending_invoices ?? 0} factura(s) pendiente(s)
          </small>
        </article>

        <article className="billing-summary-card overdue">
          <span>Saldo vencido</span>
          <strong>{money(summary?.total_overdue)}</strong>
          <small>
            {summary?.overdue_invoices ?? 0} factura(s) vencida(s)
          </small>
        </article>
      </section>

      <section className="billing-card billing-filters">
        <div className="billing-search">
          <label htmlFor="billing-search">Buscar factura o cliente</label>

          <input
            id="billing-search"
            type="text"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Número, cliente o documento"
          />
        </div>

        <div className="billing-filter-field">
          <label htmlFor="billing-status">Estado</label>

          <select
            id="billing-status"
            value={statusFilter}
            onChange={(event) => {
              setStatusFilter(event.target.value);
              setOnlyOverdue(false);
            }}
          >
            <option value="">Todos</option>
            <option value="issued">Pendiente</option>
            <option value="partial_paid">Pago parcial</option>
            <option value="paid">Pagada</option>
            <option value="overdue">Vencida</option>
            <option value="cancelled">Anulada</option>
          </select>
        </div>

        <label className="billing-overdue-check">
          <input
            type="checkbox"
            checked={onlyOverdue}
            onChange={(event) => {
              setOnlyOverdue(event.target.checked);

              if (event.target.checked) {
                setStatusFilter("");
              }
            }}
          />
          Solo vencidas
        </label>

        <button
          type="button"
          className="btn-refresh-invoices"
          onClick={refreshBilling}
        >
          ↻ Actualizar
        </button>
      </section>

      <section className="billing-card invoice-list-card">
        <div className="invoice-list-heading">
          <div>
            <h2>Facturas emitidas</h2>
            <p>{invoices.length} resultado(s)</p>
          </div>
        </div>

        <div className="invoice-table-wrapper">
          <table className="invoice-table">
            <thead>
              <tr>
                <th>Factura</th>
                <th>Cliente</th>
                <th>Emisión</th>
                <th>Vencimiento</th>
                <th>Estado</th>
                <th>Total</th>
                <th>Saldo</th>
                <th>Acciones</th>
              </tr>
            </thead>

            <tbody>
              {invoices.length === 0 ? (
                <tr>
                  <td colSpan="8" className="no-invoices">
                    No hay facturas para mostrar.
                  </td>
                </tr>
              ) : (
                invoices.map((invoice) => (
                  <tr key={invoice.id}>
                    <td>
                      <strong>{invoice.number}</strong>
                    </td>

                    <td>
                      <span>{invoice.customer_name}</span>
                      {invoice.customer_document && (
                        <small>{invoice.customer_document}</small>
                      )}
                    </td>

                    <td>
                      {new Date(
                        `${invoice.issue_date}T00:00:00`
                      ).toLocaleDateString("es-CO")}
                    </td>

                    <td>
                      {invoice.due_date
                        ? new Date(
                          `${invoice.due_date}T00:00:00`
                        ).toLocaleDateString("es-CO")
                        : "No aplica"}
                    </td>

                    <td>
                      <span
                        className={`invoice-status invoice-status-${invoice.status}`}
                      >
                        {statusLabel(invoice.status)}
                      </span>
                    </td>

                    <td>{money(invoice.total)}</td>

                    <td>
                      <strong
                        className={
                          Number(invoice.pending_amount) > 0
                            ? "invoice-pending"
                            : "invoice-paid"
                        }
                      >
                        {money(invoice.pending_amount)}
                      </strong>
                    </td>

                    <td className="invoice-actions">
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={() => openInvoiceDetail(invoice.id)}
                      >
                        Ver
                      </button>

                      {Number(invoice.pending_amount) > 0 &&
                        invoice.status !== "cancelled" &&
                        canManageBilling && (
                          <button
                            type="button"
                            className="btn-add-payment"
                            onClick={() =>
                              openInvoiceDetail(
                                invoice.id,
                                true
                              )
                            }
                          >
                            Abonar
                          </button>
                        )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      {showInvoiceForm && (
        <div className="billing-modal-backdrop">
          <section className="billing-modal">
            <button
              type="button"
              className="billing-modal-close"
              onClick={() => setShowInvoiceForm(false)}
              disabled={savingInvoice}
            >
              ×
            </button>

            <h2>Emitir factura desde venta</h2>

            <p>
              La factura copiará automáticamente los productos y pagos de la venta seleccionada.
            </p>

            {invoiceSettings && (

              <div className="invoice-config-card">

                <div className="invoice-config-item">

                  <span>Factura</span>

                  <strong>

                    {invoiceSettings.invoice_prefix}-
                    {String(invoiceSettings.current_number).padStart(6, "0")}

                  </strong>

                </div>

                <div className="invoice-config-item">

                  <span>Resolución</span>

                  <strong>

                    {invoiceSettings.resolution_number || "No configurada"}

                  </strong>

                </div>

                <div className="invoice-config-item">

                  <span>Rango autorizado</span>

                  <strong>

                    {(invoiceSettings.authorized_from || 1)}
                    {" - "}
                    {(invoiceSettings.authorized_to || "∞")}

                  </strong>

                </div>

                <div className="invoice-config-item">

                  <span>Vigencia</span>

                  <strong>

                    {invoiceSettings.valid_until || "Sin definir"}

                  </strong>

                </div>

              </div>

            )}
            {canManageBilling ? (
              <form onSubmit={issueInvoice}>
                <div className="billing-form-grid">
                  <div className="billing-field billing-field-full">
                    <label htmlFor="sale_id">Venta</label>

                    <select
                      id="sale_id"
                      name="sale_id"
                      value={invoiceForm.sale_id}
                      onChange={handleInvoiceFormChange}
                      required
                    >
                      <option value="">Selecciona una venta</option>

                      {sales
                        .filter((sale) => sale.status !== "cancelled")
                        .map((sale) => (
                          <option key={sale.id} value={sale.id}>
                            Venta #{sale.id} · {money(sale.total)} ·{" "}
                            {sale.customer_name || "Cliente general"}
                          </option>
                        ))}
                    </select>
                  </div>

                  <div className="billing-field">
                    <label htmlFor="customer_name">Cliente</label>

                    <input
                      id="customer_name"
                      name="customer_name"
                      type="text"
                      value={invoiceForm.customer_name}
                      onChange={handleInvoiceFormChange}
                      placeholder="Nombre o razón social"
                      required
                    />
                  </div>

                  <div className="billing-field">
                    <label htmlFor="customer_document">Documento</label>

                    <input
                      id="customer_document"
                      name="customer_document"
                      type="text"
                      value={invoiceForm.customer_document}
                      onChange={handleInvoiceFormChange}
                      placeholder="NIT o identificación"
                    />
                  </div>

                  <div className="billing-field">
                    <label htmlFor="customer_email">Correo</label>

                    <input
                      id="customer_email"
                      name="customer_email"
                      type="email"
                      value={invoiceForm.customer_email}
                      onChange={handleInvoiceFormChange}
                      placeholder="cliente@correo.com"
                    />
                  </div>

                  <div className="billing-field">
                    <label htmlFor="due_date">Vencimiento</label>

                    <input
                      id="due_date"
                      name="due_date"
                      type="date"
                      value={invoiceForm.due_date}
                      onChange={handleInvoiceFormChange}
                    />
                  </div>

                  <div className="billing-field">
                    <label htmlFor="tax">
                      Impuesto incluido
                    </label>

                    <input
                      id="tax"
                      name="tax"
                      type="number"
                      min="0"
                      step="0.01"
                      value={invoiceForm.tax}
                      onChange={handleInvoiceFormChange}
                    />
                  </div>

                  <div className="billing-field billing-field-full">
                    <label htmlFor="notes">Observaciones</label>

                    <textarea
                      id="notes"
                      name="notes"
                      rows="3"
                      value={invoiceForm.notes}
                      onChange={handleInvoiceFormChange}
                      placeholder="Observaciones de la factura"
                    />
                  </div>
                </div>

                <div className="billing-modal-actions">
                  <button
                    type="button"
                    className="btn-billing-secondary"
                    onClick={() => setShowInvoiceForm(false)}
                    disabled={savingInvoice}
                  >
                    Cancelar
                  </button>

                  <button
                    type="submit"
                    className="btn-confirm-invoice"
                    disabled={savingInvoice}
                  >
                    {savingInvoice
                      ? "Emitiendo..."
                      : "Emitir factura"}
                  </button>
                </div>
              </form>
            ) : (
              <div className="billing-readonly-message">
                No tienes permiso para emitir facturas.
              </div>
            )}
          </section>
        </div>
      )}

      {selectedInvoice &&
        showPaymentForm &&
        canManageBilling && (
          <div className="billing-modal-backdrop">
            <section className="billing-modal payment-modal">
              <div className="billing-modal-header">
                <div>
                  <h2>Registrar abono</h2>
                  <p>
                    Factura{" "}
                    {selectedInvoice.invoice?.number || ""}
                  </p>
                </div>

                <button
                  type="button"
                  className="billing-modal-close"
                  onClick={() => {
                    setSelectedInvoice(null);
                    setShowPaymentForm(false);
                    setPaymentForm(initialPaymentForm);
                  }}
                >
                  ×
                </button>
              </div>

              <form onSubmit={registerPayment}>
                <div className="billing-form-grid">
                  <div className="billing-field">
                    <label htmlFor="payment_method">
                      Método de pago
                    </label>

                    <select
                      id="payment_method"
                      name="payment_method"
                      value={paymentForm.payment_method}
                      onChange={handlePaymentFormChange}
                    >
                      <option value="cash">Efectivo</option>
                      <option value="card">Tarjeta</option>
                      <option value="transfer">
                        Transferencia
                      </option>
                    </select>
                  </div>

                  <div className="billing-field">
                    <label htmlFor="payment_amount">
                      Valor del abono
                    </label>

                    <input
                      id="payment_amount"
                      type="number"
                      name="amount"
                      min="0.01"
                      step="0.01"
                      max={
                        Number(
                          selectedInvoice.invoice?.pending_amount ||
                          0
                        )
                      }
                      value={paymentForm.amount}
                      onChange={handlePaymentFormChange}
                      required
                    />
                  </div>

                  <div className="billing-field billing-field-full">
                    <label htmlFor="payment_note">
                      Observación
                    </label>

                    <textarea
                      id="payment_note"
                      name="note"
                      rows="3"
                      value={paymentForm.note}
                      onChange={handlePaymentFormChange}
                      placeholder="Información adicional del abono"
                    />
                  </div>
                </div>

                <div className="billing-modal-actions">
                  <button
                    type="button"
                    className="btn-billing-secondary"
                    onClick={() => {
                      setSelectedInvoice(null);
                      setShowPaymentForm(false);
                      setPaymentForm(initialPaymentForm);
                    }}
                    disabled={savingPayment}
                  >
                    Cancelar
                  </button>

                  <button
                    type="submit"
                    className="btn-confirm-invoice"
                    disabled={savingPayment}
                  >
                    {savingPayment
                      ? "Registrando..."
                      : "Registrar abono"}
                  </button>
                </div>
              </form>
            </section>
          </div>
        )}

      {selectedInvoice && !showPaymentForm && (
        <InvoiceViewer
          invoice={selectedInvoice.invoice}
          company={selectedInvoice.company}
          invoiceSettings={
            selectedInvoice.invoice_settings
          }
          customer={selectedInvoice.customer}
          summary={selectedInvoice.summary}
          onClose={() => {
            setSelectedInvoice(null);
            setShowPaymentForm(false);
            setPaymentForm(initialPaymentForm);
          }}
        />
      )}
    </div>
  );
}

export default CompanyBilling;